import { __ } from '../../utils';
import { HeaderIconButton } from './HeaderIconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Erb,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { Link } from 'react-router';

/**
 * Kontakt na prevádzkovateľa. Jedna adresa pre otázky aj chyby: občan, ktorý
 * narazí na chybu, nemá rozlišovať, komu ju poslať - kto ju prečíta, ju
 * posunie ďalej.
 */
const CONTACT_EMAIL = 'podpora@presov.sk';

/**
 * Zásady ochrany osobných údajov visia na auth-serveri, lebo sa týkajú účtu,
 * ktorý vzniká tam (moj-presov#22). Natvrdo produkčná adresa: je to jeden
 * právny dokument, nie adresa prostredia.
 */
const PRIVACY_POLICY_URL = 'https://auth.presov.sk/ochrana-osobnych-udajov';

/** Riadok „štítok - hodnota" v dialógu. */
const Row: React.FC<React.PropsWithChildren<{ label: string }>> = ({
  label,
  children,
}) => (
  <Stack gap={0.25}>
    <Typography
      variant='overline'
      sx={{ color: 'text.secondary', fontWeight: 700, lineHeight: 1.6 }}
    >
      {label}
    </Typography>
    <Typography variant='body2' component='div'>
      {children}
    </Typography>
  </Stack>
);

/**
 * Ikona ⓘ v hlavičke a dialóg „O aplikácii" - kto appku prevádzkuje a kam sa
 * obrátiť (moj-presov#11, bod 36). Stojí pred zdieľaním, rovnakým tlačidlom
 * ako ono ([[ShareButton]]), a tlačou sa neukazuje: na papieri nemá čo otvoriť.
 */
const AboutButton: React.FC = () => {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <HeaderIconButton
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
        aria-label={__('O aplikácii')}
        className='screen-only'
      >
        <InfoOutlinedIcon
          sx={{ color: (theme) => theme.palette.primary.contrastText }}
        />
      </HeaderIconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          <Stack direction='row' alignItems='center' gap={1.5}>
            <Erb size={32} />
            <Box component='span'>{__('O aplikácii Môj Prešov')}</Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              {__(
                'Môj Prešov je aplikácia mesta Prešov pre jeho obyvateľov. Nahlásite v nej problém v meste a sledujete, ako ho mesto rieši, zapojíte sa do hlasovaní a dozviete sa novinky z mesta.',
              )}
            </Typography>

            <Row label={__('Prevádzkovateľ')}>{__('Mesto Prešov')}</Row>

            <Row label={__('Ochrana osobných údajov')}>
              <Box
                component='a'
                href={PRIVACY_POLICY_URL}
                target='_blank'
                rel='noopener noreferrer'
                sx={{ color: 'primary.main', fontWeight: 600 }}
              >
                {__('Zásady ochrany osobných údajov')}
              </Box>
            </Row>

            <Row label={__('Kontakt')}>
              <Box
                component='a'
                href={`mailto:${CONTACT_EMAIL}`}
                sx={{ color: 'primary.main', fontWeight: 600 }}
              >
                {CONTACT_EMAIL}
              </Box>
              <Typography
                variant='caption'
                component='p'
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                {__(
                  'Otázky k aplikácii, nájdené chyby aj návrhy na zlepšenie nám pošlite e-mailom, alebo ich nahláste ako',
                )}{' '}
                {/* Podnet je rovnocenná cesta, nie náhradná: dostane ho úrad
                    tak ako každý iný a občan vidí, ako sa rieši. Klik dialóg
                    zavrie, inak by zostal visieť nad formulárom. */}
                <Box
                  component={Link}
                  to='/podnety/novy'
                  onClick={() => setOpen(false)}
                  sx={{ color: 'primary.main', fontWeight: 600 }}
                >
                  {__('podnet')}
                </Box>
                .
              </Typography>
            </Row>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button variant='text' onClick={() => setOpen(false)}>
            {__('Zavrieť')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AboutButton;
